(function (root) {
  'use strict';

  const DAY_MS = 24 * 60 * 60 * 1000;
  const PROVIDER_CACHE_TTL_MS = 30 * DAY_MS;
  const API_BASE_URL = 'https://api.frankfurter.dev/v2/rate';
  const PROVIDERS_URL = 'https://api.frankfurter.dev/v2/providers';
  const CACHE_PREFIX = 'pt-local-currency:v2';
  const euroCountries = new Set([
    'AT',
    'BE',
    'CY',
    'DE',
    'EE',
    'ES',
    'FI',
    'FR',
    'GR',
    'HR',
    'IE',
    'IT',
    'LT',
    'LU',
    'LV',
    'MC',
    'MT',
    'NL',
    'PT',
    'SI',
    'SK',
  ]);
  const countryCurrencies = {
    AE: 'AED',
    AR: 'ARS',
    AT: 'EUR',
    AU: 'AUD',
    BE: 'EUR',
    BH: 'BHD',
    BR: 'BRL',
    CA: 'CAD',
    CH: 'CHF',
    CL: 'CLP',
    CN: 'CNY',
    CO: 'COP',
    CY: 'EUR',
    CZ: 'CZK',
    DE: 'EUR',
    DK: 'DKK',
    EE: 'EUR',
    ES: 'EUR',
    FI: 'EUR',
    FR: 'EUR',
    GB: 'GBP',
    GR: 'EUR',
    HK: 'HKD',
    HR: 'EUR',
    HU: 'HUF',
    ID: 'IDR',
    IE: 'EUR',
    IL: 'ILS',
    IN: 'INR',
    IS: 'ISK',
    IT: 'EUR',
    JP: 'JPY',
    KR: 'KRW',
    KW: 'KWD',
    LT: 'EUR',
    LU: 'EUR',
    LV: 'EUR',
    MC: 'EUR',
    MT: 'EUR',
    MX: 'MXN',
    MY: 'MYR',
    NL: 'EUR',
    NO: 'NOK',
    NZ: 'NZD',
    OM: 'OMR',
    PE: 'PEN',
    PH: 'PHP',
    PL: 'PLN',
    PT: 'EUR',
    QA: 'QAR',
    RO: 'RON',
    SA: 'SAR',
    SE: 'SEK',
    SG: 'SGD',
    SI: 'EUR',
    SK: 'EUR',
    TH: 'THB',
    TR: 'TRY',
    TW: 'TWD',
    US: 'USD',
    VN: 'VND',
    ZA: 'ZAR',
  };

  function normalizeCurrency(value) {
    const currency = String(value || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(currency) ? currency : '';
  }

  function countryToCurrency(countryCode) {
    return countryCurrencies[String(countryCode || '').trim().toUpperCase()] || '';
  }

  function localeToCountry(localeCode) {
    const locale = String(localeCode || '').trim().toLowerCase();
    if (locale === 'en' || locale.startsWith('en-')) return 'US';
    if (locale === 'ja' || locale.startsWith('ja-')) return 'JP';
    if (locale === 'ko' || locale.startsWith('ko-')) return 'KR';
    if (locale === 'zh-cn' || locale === 'zh-hans' || locale.startsWith('zh-cn-')) return 'CN';
    return '';
  }

  function convertMinorUnits(minorUnits, rate) {
    const amount = Number(minorUnits);
    const numericRate = Number(rate);
    if (!Number.isFinite(amount) || !Number.isFinite(numericRate) || numericRate <= 0) return null;
    return (amount / 100) * numericRate;
  }

  function cacheKey(base, quote, provider) {
    return `${CACHE_PREFIX}:rate:${provider || 'blend'}:${base}:${quote}`;
  }

  function readCachedRate(storage, key, now, ttlMs) {
    if (!storage) return null;

    try {
      const cached = JSON.parse(storage.getItem(key) || 'null');
      const fetchedAt = Number(cached && cached.fetchedAt);
      const rate = Number(cached && cached.rate);
      if (
        !Number.isFinite(fetchedAt) ||
        !Number.isFinite(rate) ||
        rate <= 0 ||
        fetchedAt > now + 5 * 60 * 1000 ||
        now - fetchedAt >= ttlMs
      ) {
        return null;
      }
      return {
        rate,
        date: cached.date || '',
        fetchedAt,
        fromCache: true,
        provider: cached.provider || '',
      };
    } catch (error) {
      return null;
    }
  }

  function writeCachedRate(storage, key, value) {
    if (!storage) return;
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (error) {
      // Local currency is an optional display enhancement.
    }
  }

  function fetchJson(fetchImpl, url, requestTimeoutMs) {
    if (typeof fetchImpl !== 'function') return Promise.reject(new Error('Fetch is unavailable'));

    const abortController = typeof AbortController === 'function' ? new AbortController() : null;
    const timeoutId = abortController
      ? setTimeout(() => abortController.abort(), requestTimeoutMs)
      : null;

    return fetchImpl(url, {
      headers: { Accept: 'application/json' },
      cache: 'default',
      signal: abortController ? abortController.signal : undefined,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Exchange rate request failed: ${response.status}`);
        return response.json();
      })
      .finally(() => {
        if (timeoutId !== null) clearTimeout(timeoutId);
      });
  }

  function providerCountryCode(countryCode) {
    const country = String(countryCode || '').trim().toUpperCase();
    return euroCountries.has(country) ? 'EU' : country;
  }

  function selectCountryProvider(providers, countryCode, baseValue, quoteValue) {
    const country = providerCountryCode(countryCode);
    const base = normalizeCurrency(baseValue);
    const quote = normalizeCurrency(quoteValue);
    if (!Array.isArray(providers) || !country || !base || !quote) return null;

    const cadenceScore = { daily: 3, weekly: 2, monthly: 1 };
    return (
      providers
        .filter((provider) => {
          const currencies = Array.isArray(provider.currencies) ? provider.currencies : [];
          const pivot = normalizeCurrency(provider.pivot_currency);
          return (
            provider.country_code === country &&
            currencies.includes(quote) &&
            (currencies.includes(base) || (pivot && currencies.includes(pivot)))
          );
        })
        .sort((left, right) => {
          const leftCurrencies = Array.isArray(left.currencies) ? left.currencies : [];
          const rightCurrencies = Array.isArray(right.currencies) ? right.currencies : [];
          const directDifference = Number(rightCurrencies.includes(base)) - Number(leftCurrencies.includes(base));
          if (directDifference) return directDifference;

          const cadenceDifference =
            (cadenceScore[right.publish_cadence] || 0) - (cadenceScore[left.publish_cadence] || 0);
          if (cadenceDifference) return cadenceDifference;
          return String(right.end_date || '').localeCompare(String(left.end_date || ''));
        })[0] || null
    );
  }

  function createRateLoader(options) {
    const settings = options || {};
    const fetchImpl = settings.fetchImpl;
    const storage = settings.storage || null;
    const now = settings.now || Date.now;
    const ttlMs = settings.ttlMs || DAY_MS;
    const requestTimeoutMs = settings.requestTimeoutMs || 8000;
    const apiBaseUrl = settings.apiBaseUrl || API_BASE_URL;
    const inFlight = new Map();

    return function loadRate(baseValue, quoteValue, providerValue) {
      const base = normalizeCurrency(baseValue);
      const quote = normalizeCurrency(quoteValue);
      const provider = String(providerValue || '').trim().toUpperCase();
      if (!base || !quote) return Promise.reject(new Error('Invalid currency code'));
      if (provider && !/^[A-Z0-9_-]+$/.test(provider)) {
        return Promise.reject(new Error('Invalid exchange-rate provider'));
      }
      if (base === quote) return Promise.resolve({ rate: 1, date: '', fromCache: true });

      const key = cacheKey(base, quote, provider);
      const cached = readCachedRate(storage, key, now(), ttlMs);
      if (cached) return Promise.resolve(cached);
      if (inFlight.has(key)) return inFlight.get(key);
      const providerQuery = provider ? `?providers=${encodeURIComponent(provider)}` : '';
      const request = fetchJson(fetchImpl, `${apiBaseUrl}/${base}/${quote}${providerQuery}`, requestTimeoutMs)
        .then((data) => {
          const rate = Number(data && data.rate);
          if (!Number.isFinite(rate) || rate <= 0) throw new Error('Invalid exchange rate response');

          const value = {
            rate,
            date: data.date || '',
            fetchedAt: now(),
            fromCache: false,
            provider,
          };
          writeCachedRate(storage, key, value);
          return value;
        })
        .finally(() => {
          inFlight.delete(key);
        });

      inFlight.set(key, request);
      return request;
    };
  }

  function createProviderLoader(options) {
    const settings = options || {};
    const fetchImpl = settings.fetchImpl;
    const storage = settings.storage || null;
    const now = settings.now || Date.now;
    const ttlMs = settings.ttlMs || PROVIDER_CACHE_TTL_MS;
    const requestTimeoutMs = settings.requestTimeoutMs || 8000;
    const providersUrl = settings.providersUrl || PROVIDERS_URL;
    const key = `${CACHE_PREFIX}:providers`;
    let inFlight = null;

    return function loadProviders() {
      if (storage) {
        try {
          const cached = JSON.parse(storage.getItem(key) || 'null');
          if (
            cached &&
            Array.isArray(cached.providers) &&
            Number.isFinite(Number(cached.fetchedAt)) &&
            now() - Number(cached.fetchedAt) < ttlMs
          ) {
            return Promise.resolve(cached.providers);
          }
        } catch (error) {
          // Ignore unavailable or malformed provider cache.
        }
      }

      if (inFlight) return inFlight;
      inFlight = fetchJson(fetchImpl, providersUrl, requestTimeoutMs)
        .then((providers) => {
          if (!Array.isArray(providers)) throw new Error('Invalid exchange-rate provider response');
          if (storage) {
            try {
              storage.setItem(key, JSON.stringify({ providers, fetchedAt: now() }));
            } catch (error) {
              // Provider discovery can continue without persistent storage.
            }
          }
          return providers;
        })
        .finally(() => {
          inFlight = null;
        });
      return inFlight;
    };
  }

  function loadCountryRate(options) {
    const settings = options || {};
    const base = normalizeCurrency(settings.base);
    const quote = normalizeCurrency(settings.quote);
    const country = String(settings.country || '').trim().toUpperCase();
    const loadRate = settings.loadRate;
    const loadProviders = settings.loadProviders;
    if (!base || !quote || typeof loadRate !== 'function' || typeof loadProviders !== 'function') {
      return Promise.reject(new Error('Invalid country-rate loader configuration'));
    }

    return loadProviders()
      .then((providers) => selectCountryProvider(providers, country, base, quote))
      .catch(() => null)
      .then((provider) => {
        if (!provider) return loadRate(base, quote);
        const currencies = Array.isArray(provider.currencies) ? provider.currencies : [];
        if (currencies.includes(base)) {
          return loadRate(base, quote, provider.key).catch(() => loadRate(base, quote));
        }

        const pivot = normalizeCurrency(provider.pivot_currency);
        if (!pivot || !currencies.includes(pivot)) return loadRate(base, quote);
        return Promise.all([loadRate(base, pivot), loadRate(pivot, quote, provider.key)])
          .then(([baseRate, quoteRate]) => ({
            rate: baseRate.rate * quoteRate.rate,
            date: quoteRate.date || baseRate.date,
            fetchedAt: Math.max(Number(baseRate.fetchedAt) || 0, Number(quoteRate.fetchedAt) || 0),
            fromCache: Boolean(baseRate.fromCache && quoteRate.fromCache),
            provider: provider.key,
          }))
          .catch(() => loadRate(base, quote));
      });
  }

  const api = {
    DAY_MS,
    cacheKey,
    countryToCurrency,
    convertMinorUnits,
    createProviderLoader,
    createRateLoader,
    loadCountryRate,
    localeToCountry,
    readCachedRate,
    selectCountryProvider,
  };

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (!root || !root.document) return;

  const configElement = root.document.querySelector('[data-pt-local-currency-config]');
  if (!configElement) return;

  let config;
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const baseCurrency = normalizeCurrency(config.baseCurrency);
  let countryCode = localeToCountry(config.locale) || String(config.country || '').trim().toUpperCase();
  let quoteCurrency = countryToCurrency(countryCode);
  if (['localhost', '127.0.0.1'].includes(root.location.hostname)) {
    const demoParameters = new URLSearchParams(root.location.search);
    const demoCountry = String(demoParameters.get('pt_country') || '').trim().toUpperCase();
    if (/^[A-Z]{2}$/.test(demoCountry)) countryCode = demoCountry;
    quoteCurrency = normalizeCurrency(demoParameters.get('pt_currency')) || countryToCurrency(countryCode);
  }
  if (!baseCurrency || !quoteCurrency || baseCurrency === quoteCurrency) return;

  let storage = null;
  try {
    storage = root.localStorage;
  } catch (error) {
    // Some privacy modes block storage access entirely.
  }
  const loadRate = createRateLoader({
    fetchImpl: typeof root.fetch === 'function' ? root.fetch.bind(root) : null,
    storage,
  });
  const loadProviders = createProviderLoader({
    fetchImpl: typeof root.fetch === 'function' ? root.fetch.bind(root) : null,
    storage,
  });
  const ratePromise = loadCountryRate({
    base: baseCurrency,
    quote: quoteCurrency,
    country: countryCode,
    loadRate,
    loadProviders,
  }).catch((error) => {
    if (['localhost', '127.0.0.1'].includes(root.location.hostname)) {
      root.console.warn('[PaperTracker] Local currency estimate is unavailable.', error);
    }
    return null;
  });
  const formatter = new Intl.NumberFormat(config.locale || undefined, {
    style: 'currency',
    currency: quoteCurrency,
    currencyDisplay: 'symbol',
  });

  function matchingPriceElements(node) {
    const elements = [];
    if (node.nodeType !== 1 && node.nodeType !== 9) return elements;
    if (node.matches && node.matches('[data-pt-local-price-cents]')) elements.push(node);
    return elements.concat(Array.from(node.querySelectorAll('[data-pt-local-price-cents]')));
  }

  function decorate(node) {
    const elements = matchingPriceElements(node).filter(
      (element) => element.dataset.ptLocalCurrencyReady !== quoteCurrency,
    );
    if (!elements.length) return;

    ratePromise.then((result) => {
      if (!result) return;
      elements.forEach((element) => {
        const converted = convertMinorUnits(element.dataset.ptLocalPriceCents, result.rate);
        const convertedMax = element.dataset.ptLocalPriceMaxCents
          ? convertMinorUnits(element.dataset.ptLocalPriceMaxCents, result.rate)
          : null;
        const output = element.querySelector('[data-pt-local-currency]');
        if (converted === null || !output) return;

        element.dataset.ptLocalCurrencyReady = quoteCurrency;
        const convertedPrice =
          convertedMax !== null && convertedMax !== converted
            ? `${formatter.format(converted)} – ${formatter.format(convertedMax)}`
            : formatter.format(converted);
        output.textContent = `≈ ${convertedPrice} · ${config.estimateLabel}`;
        output.title = config.checkoutNote;
        output.dataset.ptLocalCurrencyProvider = result.provider || 'blend';
        output.dataset.ptLocalCurrencyDate = result.date || '';
        output.hidden = false;
      });
    });
  }

  let frameId = null;
  function scheduleDecoration() {
    if (frameId !== null) return;
    frameId = root.requestAnimationFrame(() => {
      frameId = null;
      decorate(root.document);
    });
  }

  decorate(root.document);
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        delete mutation.target.dataset.ptLocalCurrencyReady;
      }
    });
    scheduleDecoration();
  }).observe(root.document.getElementById('MainContent') || root.document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-pt-local-price-cents', 'data-pt-local-price-max-cents'],
  });
})(typeof window !== 'undefined' ? window : null);
